import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { loginApi, logoutApi } from '../api/auth';
import axios from '../api/axios';
 
const AuthContext = createContext(null);
const ACCOUNT_SUSPENDED_MESSAGE = 'Your account has been temporarily suspended. Please contact support.';
 
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
 
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
 
  const [loading, setLoading] = useState(false);
 
  const isAuthenticated = !!user && !!localStorage.getItem('access_token');

  const clearSession = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);
 
  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await loginApi(email, password);

      // 2FA required — return challenge data without saving tokens
      if (res.data?.requires_2fa) {
        return { requires_2fa: true, pre_auth_token: res.data.pre_auth_token };
      }

      const { access_token, refresh_token, user: userData } = res.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } finally {
      setLoading(false);
    }
  }, []);
 
  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // ignore
    }
    clearSession();
  }, [clearSession]);
 
  // Always refresh user from server on app start to get fresh avatar_url etc.
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    axios.get('/users/me')
      .then(res => {
        const fresh = res.data;
        localStorage.setItem('user', JSON.stringify(fresh));
        setUser(fresh);
      })
      .catch(() => {
        // Token expired or invalid — leave as-is, refresh flow handles it
      });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const verifyAccountStatus = () => {
      axios.get('/users/me').catch((err) => {
        if (err.response?.status === 403 && err.response?.data?.detail === ACCOUNT_SUSPENDED_MESSAGE) {
          clearSession();
        }
      });
    };

    const interval = setInterval(verifyAccountStatus, 30000);
    return () => clearInterval(interval);
  }, [clearSession, isAuthenticated]);
 
  const updateUser = useCallback((userDataOrFn) => {
    setUser((prev) => {
      const newData = typeof userDataOrFn === 'function' ? userDataOrFn(prev) : userDataOrFn;
      localStorage.setItem('user', JSON.stringify(newData));
      return newData;
    });
  }, []);
 
  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
